import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "form",
    "button",
    "formButton",
    "submitButton",
    "termsAccepted",
    "privacyAccepted",
    "nationality",
    "documentType",
    "documentNumber",
    "documentCountry",
  ];

  connect() {
    this.saveOriginalSubmitButtonText();
    this.resetSubmitButtonLoader();

    this.boundResetLoader = this.resetSubmitButtonLoader.bind(this);
    this.boundInputHandler = this.handleInputChange.bind(this);

    this.element.addEventListener("turbo:submit-end", this.boundResetLoader);
    this.element.addEventListener("ajax:complete", this.boundResetLoader);
    this.element.addEventListener("ajax:error", this.boundResetLoader);

    this.element.addEventListener("input", this.boundInputHandler);
    this.element.addEventListener("change", this.boundInputHandler);
  }

  disconnect() {
    clearTimeout(this.submitLoaderTimeout);

    if (this.boundResetLoader) {
      this.element.removeEventListener("turbo:submit-end", this.boundResetLoader);
      this.element.removeEventListener("ajax:complete", this.boundResetLoader);
      this.element.removeEventListener("ajax:error", this.boundResetLoader);
    }

    if (this.boundInputHandler) {
      this.element.removeEventListener("input", this.boundInputHandler);
      this.element.removeEventListener("change", this.boundInputHandler);
    }
  }

  showForm() {
    this.formTarget.classList.remove("d-none");
    this.formTarget.classList.add("d-block");
    this.formButtonTarget.classList.remove("d-none");
    this.formButtonTarget.classList.add("d-block");

    this.buttonTarget.classList.add("d-none");
  }

  handleInputChange() {
    if (!this.hasSubmitButtonTarget) return;
    if (!this.submitButtonTarget.disabled) return;

    this.resetSubmitButtonLoader();
  }

  prepareSubmission(event) {
    this.syncIdentityFields();

    const termsAccepted = this.termsAcceptedTarget.checked;
    const privacyAccepted = this.privacyAcceptedTarget.checked;

    if (!termsAccepted || !privacyAccepted) {
      event.preventDefault();
      this.resetSubmitButtonLoader();
      alert(
        "Você precisa aceitar os termos e a política de privacidade para continuar."
      );
      return;
    }

    if (!this.identityCompleted()) {
      event.preventDefault();
      this.resetSubmitButtonLoader();
      alert(
        "Você precisa informar o país e o documento fiscal para continuar."
      );
      return;
    }

    if (this.documentNumberTarget.classList.contains("is-invalid")) {
      event.preventDefault();
      this.resetSubmitButtonLoader();
      alert("O documento fiscal informado não é válido.");
      return;
    }

    const form = event.target.closest("form");
    if (this.isOmniauthForm(form)) {
      this.appendOmniauthContext(form);
    }

    this.setSubmitButtonLoader(event);
  }

  saveOriginalSubmitButtonText() {
    if (!this.hasSubmitButtonTarget) return;

    const button = this.submitButtonTarget;
    if (button.dataset.originalText) return;

    const text = button.tagName === "INPUT" ? button.value : button.innerHTML;
    button.dataset.originalText = text;
  }

  setSubmitButtonLoader(event) {
    if (!this.hasSubmitButtonTarget) return;

    this.saveOriginalSubmitButtonText();
    clearTimeout(this.submitLoaderTimeout);

    this.submitLoaderTimeout = setTimeout(() => {
      if (event?.defaultPrevented) {
        this.resetSubmitButtonLoader();
        return;
      }

      const button = this.submitButtonTarget;
      button.disabled = true;

      if (button.tagName === "INPUT") {
        button.value = "Processando...";
        return;
      }

      button.innerHTML = `<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Processando...`;
    }, 0);
  }

  resetSubmitButtonLoader() {
    if (!this.hasSubmitButtonTarget) return;

    clearTimeout(this.submitLoaderTimeout);
    const button = this.submitButtonTarget;

    button.disabled = false;
    button.removeAttribute("disabled");

    const originalText = button.dataset.originalText;
    if (!originalText) return;

    if (button.tagName === "INPUT") {
      button.value = originalText;
      return;
    }

    button.innerHTML = originalText;
  }

  syncIdentityFields() {
    const identityValues = {
      nationality: this.nationalityTarget.value,
      country: this.documentCountryTarget.value,
      docType: this.documentTypeTarget.value,
      docNumber: this.documentNumberTarget.value,
    };

    this.element
      .querySelectorAll("[data-registration-sync-field]")
      .forEach((input) => {
        input.value = identityValues[input.dataset.registrationSyncField] || "";
      });
  }

  identityCompleted() {
    return (
      this.nationalityTarget.value &&
      this.documentTypeTarget.value &&
      this.documentNumberTarget.value.trim()
    );
  }

  isOmniauthForm(form) {
    if (!form) return false;
    return (
      form.action.includes("/users/auth/linkedin") ||
      form.action.includes("/users/auth/google_oauth2")
    );
  }

  appendOmniauthContext(form) {
    const url = new URL(form.action, window.location.origin);
    const atsRegistration =
      form.querySelector("input[name='ats_registration']")?.value || "true";
    const username = form.querySelector("input[name='username']")?.value;
    const jobCode = form.querySelector("input[name='job_code']")?.value;
    const organizationType = form.querySelector(
      "input[name='organization_type']"
    )?.value;
    const termAccepted = this.termsAcceptedTarget.checked ? "1" : "0";
    const privacyPoliciesAccepted = this.privacyAcceptedTarget.checked
      ? "1"
      : "0";

    this.setQueryParam(url, "ats_registration", atsRegistration);
    this.setQueryParam(url, "username", username);
    this.setQueryParam(url, "job_code", jobCode);
    this.setQueryParam(url, "organization_type", organizationType);
    this.setQueryParam(url, "term_accepted", termAccepted);
    this.setQueryParam(
      url,
      "privacy_policies_accepted",
      privacyPoliciesAccepted
    );
    this.setQueryParam(url, "nationality", this.nationalityTarget.value);
    this.setQueryParam(
      url,
      "document_attributes[country]",
      this.documentCountryTarget.value
    );
    this.setQueryParam(
      url,
      "document_attributes[doc_type]",
      this.documentTypeTarget.value
    );
    this.setQueryParam(
      url,
      "document_attributes[doc_number]",
      this.documentNumberTarget.value
    );

    form.action = url.toString();
  }

  setQueryParam(url, key, value) {
    if (value === undefined || value === null) return;
    const normalizedValue = String(value).trim();
    if (!normalizedValue) return;
    url.searchParams.set(key, normalizedValue);
  }
}
